"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleAlert, Droplets, Flame, Gauge, PhoneCall, Pipette, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PlumbingProblem {
  id: string;
  title: string;
  description: string;
  severity: string;
  actionText: string;
  linkPath: string;
  iconName: string;
}
const plumbingProblems: PlumbingProblem[] = [{
  id: "clogged-drains",
  title: "Clogged Drains",
  description: "Slow-draining sinks, standing tub water, or gurgling sounds signaling deep blockages in your pipes.",
  severity: "Common",
  actionText: "Drain Solutions",
  linkPath: "/drain-problems/",
  iconName: "Droplets"
}, {
  id: "water-leaks",
  title: "Water Leaks",
  description: "Hidden wall drips, damp drywall, or pooling water that leads to structural decay and mold growth.",
  severity: "High Priority",
  actionText: "Leak Detection",
  linkPath: "/water-leak-problems/",
  iconName: "Pipette"
}, {
  id: "no-hot-water",
  title: "No Hot Water",
  description: "Sudden loss of hot water, pilot light failures, or fluctuating temperatures from your water heater tank.",
  severity: "Urgent",
  actionText: "Water Heater Help",
  linkPath: "/water-heater-problems/",
  iconName: "Flame"
}, {
  id: "toilet-problems",
  title: "Toilet Problems",
  description: "Continuous running, overflowing bowls, weak flushes, or cracked porcelain leaking onto the floor.",
  severity: "High Priority",
  actionText: "Toilet Repair",
  linkPath: "/toilet-problems/",
  iconName: "CircleAlert"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  description: "Weak shower flow or trickling faucets throughout the house pointing to sediment buildup or main line faults.",
  severity: "Moderate",
  actionText: "Pressure Repair",
  linkPath: "/water-pressure-problems/",
  iconName: "Gauge"
}, {
  id: "sewer-backup",
  title: "Sewer Backup",
  description: "Foul sewer smells, multiple backed-up fixtures, or dirty wastewater rising up through floor drains.",
  severity: "Emergency",
  actionText: "Sewer Inspection",
  linkPath: "/sewer-line-repair/",
  iconName: "ShieldAlert"
}, {
  id: "burst-pipes",
  title: "Burst Pipes",
  description: "Ruptured water lines pouring water rapidly, requiring immediate main shut-off and emergency pipe repair.",
  severity: "Emergency",
  actionText: "Emergency Pipe Fix",
  linkPath: "/pipe-problems/",
  iconName: "AlertTriangle"
}, {
  id: "fixture-issues",
  title: "Fixture Issues",
  description: "Dripping faucets, broken valves, jammed garbage disposals, and loose bathroom or kitchen fixtures.",
  severity: "Standard",
  actionText: "Fixture Repair",
  linkPath: "/fixture-problems/",
  iconName: "Wrench"
}];
function ProblemIcon({
  name
}: {
  name: string;
}) {
  switch (name) {
    case "Droplets":
      return <Droplets className="size-6 text-primary" />;
    case "Pipette":
      return <Pipette className="size-6 text-primary" />;
    case "Flame":
      return <Flame className="size-6 text-primary" />;
    case "CircleAlert":
      return <CircleAlert className="size-6 text-primary" />;
    case "Gauge":
      return <Gauge className="size-6 text-primary" />;
    case "ShieldAlert":
      return <ShieldAlert className="size-6 text-destructive" />;
    case "AlertTriangle":
      return <AlertTriangle className="size-6 text-destructive" />;
    case "Wrench":
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function PlumbingIssues() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="commonplumbingissues" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Fast Troubleshooting &amp; Repairs
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Common Plumbing Issues in Jonesboro
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plumbing breakdowns disrupt your home and can cause costly property damage. Identify your issue below for rapid diagnosis and same-day 24/7 service.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plumbingProblems.map((problem, i) => {
          const isEmergency = problem.severity === "Emergency";
          return <div key={problem.id} data-index={i} className="flex">
                <motion.div className="w-full flex" initial={prefersReducedMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-50px"
            }} transition={{
              duration: 0.35,
              delay: prefersReducedMotion ? 0 : i * 0.05
            }}>
                  <Card className={`w-full flex flex-col justify-between border transition-all duration-200 hover:shadow-md ${isEmergency ? "border-destructive/30 bg-card hover:border-destructive" : "border-border bg-card hover:border-primary/40"}`}>
                    <CardHeader className="space-y-3 pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className={`p-3 rounded-lg flex items-center justify-center ${isEmergency ? "bg-destructive/10" : "bg-primary/10"}`}>
                          <ProblemIcon name={problem.iconName} />
                        </div>
                        <Badge variant={isEmergency ? "destructive" : "secondary"} className="text-[11px] font-medium">
                          {problem.severity}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground pt-1">
                        {problem.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="py-2">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {problem.description}
                      </CardDescription>
                    </CardContent>

                    <CardFooter className="pt-4 border-t border-border/50">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-foreground hover:text-primary hover:bg-transparent group">
                        <Link to={problem.linkPath} className="flex items-center justify-between w-full">
                          <span>{problem.actionText}</span>
                          <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              </div>;
        })}
        </div>

        <div className="mt-12 sm:mt-16 bg-muted/60 rounded-2xl p-6 sm:p-8 border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-default text-lg sm:text-xl font-bold text-foreground">
              Facing an active plumbing emergency?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our licensed Jonesboro plumbers are on call 24 hours a day, 7 days a week.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-2" />
                Request Emergency Help
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/problems/">View All Problems</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}